import {
  BottomSheet,
  iconSizeByAvatarSize,
  Text,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { useHistory } from "react-router-dom";

import { APP_V2_USER_EVENTS } from "../lib";
import { logEvent } from "../lib/analytics";
import { useLogEffect } from "../lib/analytics/useLogEffect";
import { useGetQualificationDisplayLabels } from "../Qualifications/api/useGetQualificationDisplayLabels";
import { Button } from "../redesign/components/Button";
import { WorkerAvatar } from "../redesign/components/WorkerAvatar";
import { WorkplaceAvatar } from "../redesign/components/WorkplaceAvatar";
import { getSafeAreaPaddings } from "../redesign/utils/getSafeAreaPaddings";
import AddFriendBottomSheet from "../redesign/WorkWithFriends/modals/AddFriend/AddFriend";
import { useDefinedWorker } from "../Worker/useDefinedWorker";
import { NewsFeedItemCommentsButton } from "./CommentsButton";
import { NewsFeedItemCommentSection } from "./CommentSection";
import { NewsFeedItemReactionsButton } from "./ReactionsButton";
import {
  type NewsFeedItem,
  type NewsFeedItemIncludedShift,
  type NewsFeedItemIncludedWorker,
  type NewsFeedItemIncludedWorkplace,
  type NewsFeedItemPostAttributes,
  NewsFeedItemPostEvent,
  NewsFeedItemRecommendationEvent,
  NewsFeedItemType,
} from "./types";
import { getNewsFeedContentLink } from "./utils/getNewsFeedContentLink";
import { getNewsFeedContentLinkText } from "./utils/getNewsFeedContentLinkText";
import { getNewsFeedContentText } from "./utils/getNewsFeedContentText";

interface NewsFeedItemContentSheetProps {
  modalState: UseModalState;
  newsFeedItem: NewsFeedItem;
  isLoadingWorkerPicture?: boolean;
  workerPictureUrl?: string;
  workplacePictureUrl?: string;
  isLoadingWorkplacePicture?: boolean;
  included: {
    includedShift?: NewsFeedItemIncludedShift;
    includedWorkplace?: NewsFeedItemIncludedWorkplace;
    includedRecommendedWorkplace?: NewsFeedItemIncludedWorkplace;
    includedWorker?: NewsFeedItemIncludedWorker;
    includedRecommendedWorker?: NewsFeedItemIncludedWorker;
    includedReferredWorker?: NewsFeedItemIncludedWorker;
  };
}
export function NewsFeedItemContentSheet(props: NewsFeedItemContentSheetProps) {
  const {
    modalState,
    newsFeedItem,
    isLoadingWorkerPicture,
    workerPictureUrl,
    isLoadingWorkplacePicture,
    workplacePictureUrl,
    included: {
      includedShift,
      includedWorker,
      includedRecommendedWorker,
      includedReferredWorker,
      includedWorkplace,
      includedRecommendedWorkplace,
    },
  } = props;

  const history = useHistory();
  const currentWorker = useDefinedWorker();

  useLogEffect(APP_V2_USER_EVENTS.NEWS_FEED_ITEM_OPENED, {
    type: newsFeedItem.attributes.type,
    event: newsFeedItem.attributes.metadata.event,
    createdAt: newsFeedItem.attributes.createdAt,
  });
  const canAddFriend = [
    NewsFeedItemPostEvent.REFERRED_NEW_WORKER_JOINED,
    NewsFeedItemRecommendationEvent.FRIEND_RECOMMENDATION,
  ].includes(newsFeedItem.attributes.metadata.event);
  const addFriendModalState = useModalState();

  const includedShiftQualification = includedShift?.attributes.qualification ?? "";

  const { qualificationNameToDisplayLabelMap } = useGetQualificationDisplayLabels([
    includedShiftQualification,
  ]);

  const shiftQualificationDisplayLabel =
    qualificationNameToDisplayLabelMap.get(includedShiftQualification) ??
    includedShiftQualification;

  return (
    <BottomSheet modalState={modalState}>
      <Stack
        pb={
          getSafeAreaPaddings({
            bottom: true,
          }).paddingBottom
        }
      >
        <Stack p={5} spacing={5}>
          <Stack direction="row" spacing={3} p={2} alignItems="center" height="100%">
            <Box sx={{ width: iconSizeByAvatarSize.medium }}>
              {newsFeedItem.attributes.metadata.event ===
              NewsFeedItemRecommendationEvent.WORKPLACE_RECOMMENDATION ? (
                <WorkplaceAvatar
                  size="large"
                  isLoading={isLoadingWorkplacePicture}
                  pictureUrl={workplacePictureUrl}
                />
              ) : (
                <WorkerAvatar
                  size="large"
                  profilePictureUrl={workerPictureUrl}
                  isLoading={isLoadingWorkerPicture}
                />
              )}
            </Box>
            <Stack justifyContent="space-between" py={1} height="100%" spacing={1}>
              <Text variant="h6">
                {getNewsFeedContentText({
                  event: newsFeedItem.attributes.metadata.event,
                  currentWorkerName: currentWorker.name,
                  workerName: includedWorker?.attributes.name,
                  recommendedWorkerName: includedRecommendedWorker?.attributes.name,
                  referredWorkerName: includedReferredWorker?.attributes.name,
                  recommendedWorkplaceName: includedRecommendedWorkplace?.attributes.name,
                  shiftQualificationDisplayLabel,
                  workplaceName: includedWorkplace?.attributes.name,
                })}
              </Text>

              <Stack>
                <Text variant="body2" color={(theme) => theme.palette.text.tertiary}>
                  {formatDistanceToNow(new Date(newsFeedItem.attributes.createdAt))} ago
                </Text>
              </Stack>
            </Stack>
          </Stack>
          {newsFeedItem.attributes.type === NewsFeedItemType.POST && (
            <Stack direction="row" justifyContent="center" alignItems="center">
              <Stack direction="row" spacing={1}>
                <NewsFeedItemReactionsButton
                  reactions={newsFeedItem.attributes.reactions}
                  newsFeedItemId={newsFeedItem.id}
                />
                <NewsFeedItemCommentsButton count={newsFeedItem.attributes.comments.length} />
              </Stack>
            </Stack>
          )}
          <Button
            variant="contained"
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.NEWS_FEED_CARD_LINK_CLICKED, {
                event: newsFeedItem.attributes.metadata.event,
                metadata: newsFeedItem.attributes.metadata,
              });

              if (canAddFriend) {
                addFriendModalState.openModal();
                return;
              }

              history.push(
                getNewsFeedContentLink({
                  event: newsFeedItem.attributes.metadata.event,
                  workplaceId: includedWorkplace?.id,
                  commentId: (newsFeedItem.attributes as NewsFeedItemPostAttributes).metadata
                    .workplaceCommentId,
                  shiftId: includedShift?.id,
                  recommendedWorkplaceId: includedRecommendedWorkplace?.id,
                })
              );
            }}
          >
            {getNewsFeedContentLinkText({
              event: newsFeedItem.attributes.metadata.event,
              workerName: includedWorker?.attributes.name,
              recommendedWorkerName: includedRecommendedWorker?.attributes.name,
              referredWorkerName: includedReferredWorker?.attributes.name,
              recommendedWorkplaceName: includedRecommendedWorkplace?.attributes.name,
              workplaceName: includedWorkplace?.attributes.name,
            })}
          </Button>
        </Stack>
        {newsFeedItem.attributes.type === NewsFeedItemType.POST && (
          <NewsFeedItemCommentSection
            newsFeedItemId={newsFeedItem.id}
            comments={newsFeedItem.attributes.comments}
          />
        )}
        {canAddFriend && (
          <AddFriendBottomSheet
            modalState={addFriendModalState}
            friendId={
              newsFeedItem.attributes.metadata.event ===
              NewsFeedItemRecommendationEvent.FRIEND_RECOMMENDATION
                ? newsFeedItem.attributes.metadata.recommendedWorkerId
                : (newsFeedItem.attributes as NewsFeedItemPostAttributes).metadata.referredWorkerId
            }
            onClose={() => {
              addFriendModalState.closeModal();
              modalState.closeModal();
            }}
          />
        )}
      </Stack>
    </BottomSheet>
  );
}
